/**
 * DREAMSTATION ESPORTS — KINETIC APEX CORE JS
 * assets/dreamstation.js · v2.0 · March 2026
 *
 * Rules:
 * - Vanilla ES6+ only. No libraries.
 * - No window.addEventListener('scroll') — IntersectionObserver only.
 * - Max single animation: 400ms.
 * - Target bundle: < 50KB gzipped.
 * - All initialisers called on DOMContentLoaded.
 */

'use strict';

/* ── 1. NAV SCROLL BEHAVIOUR ─────────────────────────────────────────────── */
/**
 * Toggles .is-scrolled on .ds-nav when user scrolls past the sentinel element.
 * Uses IntersectionObserver — no scroll event listeners.
 */
function initNavScrollBehaviour() {
  const nav = document.querySelector('.ds-nav');
  if (!nav) return;

  // Sentinel element — a zero-height div at the very top of the page
  let sentinel = document.getElementById('ds-nav-sentinel');
  if (!sentinel) {
    sentinel = document.createElement('div');
    sentinel.id = 'ds-nav-sentinel';
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:80px;pointer-events:none;';
    document.body.prepend(sentinel);
  }

  const observer = new IntersectionObserver(
    ([entry]) => nav.classList.toggle('is-scrolled', !entry.isIntersecting),
    { threshold: 0 }
  );
  observer.observe(sentinel);
}


/* ── 2. MOBILE NAV OVERLAY ───────────────────────────────────────────────── */
/**
 * Opens/closes the full-screen mobile nav overlay.
 * - Toggles aria-expanded on hamburger button.
 * - Traps focus within the overlay when open.
 * - Closes on Escape key.
 * - Returns focus to hamburger on close.
 */
function initMobileNav() {
  const hamburger = document.querySelector('.ds-nav__hamburger');
  const overlay   = document.querySelector('.ds-nav__overlay');
  if (!hamburger || !overlay) return;

  const focusableSelectors = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');

  function getFocusable() {
    return Array.from(overlay.querySelectorAll(focusableSelectors));
  }

  function openOverlay() {
    overlay.classList.add('is-open');
    hamburger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    // Focus first focusable element inside overlay
    const focusable = getFocusable();
    if (focusable.length) focusable[0].focus();
  }

  function closeOverlay() {
    overlay.classList.remove('is-open');
    hamburger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    hamburger.focus();
  }

  hamburger.addEventListener('click', () => {
    const isOpen = hamburger.getAttribute('aria-expanded') === 'true';
    isOpen ? closeOverlay() : openOverlay();
  });

  // Escape key closes overlay
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('is-open')) {
      closeOverlay();
    }
  });

  // Focus trap inside overlay
  overlay.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusable = getFocusable();
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // Handle mobile dropdown toggles (supports multiple dropdowns)
  overlay.querySelectorAll('.ds-nav__overlay-link--dropdown-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!isExpanded));
    });
  });

  // Close on overlay link click (navigation)
  overlay.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', closeOverlay);
  });
}


/* ── 3. SCROLL ANIMATIONS ───────────────────────────────────────────────── */
/**
 * Adds .is-visible to .ds-animate-in elements when they enter the viewport.
 * Fires once per element, then unobserves.
 */
function initScrollAnimations() {
  const elements = document.querySelectorAll('.ds-animate-in');
  if (!elements.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15 });

  elements.forEach(el => observer.observe(el));
}


/* ── 4. TICKER PAUSE-ON-HOVER ───────────────────────────────────────────── */
/**
 * CSS handles the pause via animation-play-state: paused on :hover.
 * This JS block handles touch-start/end for mobile users.
 */
function initTickerPause() {
  const tracks = document.querySelectorAll('.ds-ticker__track');
  tracks.forEach(track => {
    track.addEventListener('touchstart', () => {
      track.style.animationPlayState = 'paused';
    }, { passive: true });
    track.addEventListener('touchend', () => {
      track.style.animationPlayState = 'running';
    }, { passive: true });
  });
}


/* ── 5. FORM VALIDATION ──────────────────────────────────────────────────── */
/**
 * Adds .is-error class and aria-describedby on DS form fields when invalid.
 * Works on any form with data-ds-validate attribute.
 */
function initFormValidation() {
  const forms = document.querySelectorAll('form[data-ds-validate]');
  forms.forEach(form => {
    form.addEventListener('submit', (e) => {
      let hasErrors = false;
      const fields = form.querySelectorAll('.ds-input[required], .ds-textarea[required], .ds-select[required]');

      fields.forEach(field => {
        const errorEl = form.querySelector(`[data-error-for="${field.id}"]`);
        if (!field.value.trim()) {
          field.classList.add('is-error');
          if (errorEl) {
            errorEl.textContent = 'This field is required.';
            field.setAttribute('aria-describedby', errorEl.id);
          }
          hasErrors = true;
        } else {
          field.classList.remove('is-error');
          if (errorEl) errorEl.textContent = '';
        }

        // Email validation
        if (field.type === 'email' && field.value.trim()) {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(field.value.trim())) {
            field.classList.add('is-error');
            if (errorEl) errorEl.textContent = 'Enter a valid email address.';
            hasErrors = true;
          }
        }
      });

      if (hasErrors) {
        e.preventDefault();
        // Focus the first error field
        const firstError = form.querySelector('.ds-input.is-error, .ds-textarea.is-error');
        if (firstError) firstError.focus();
      }
    });

    // Clear error on input
    form.querySelectorAll('.ds-input, .ds-textarea, .ds-select').forEach(field => {
      field.addEventListener('input', () => {
        field.classList.remove('is-error');
      });
    });
  });
}


/* ── 5.5. ROSTER CROSS-PAGE SCROLL ──────────────────────────────────────── */
/**
 * ROSTER link smart navigation (Phase 1 — Page 1).
 * - On home page: smooth scroll to #ds-roster-preview
 * - On other pages: navigate to home with ?scroll=roster, then scroll on load
 * - Handles post-navigation scroll via URLSearchParams + history.replaceState
 */
function initRosterScroll() {
  // Handle ROSTER link clicks
  const rosterLink = document.querySelector('[data-roster-link="true"]');
  if (rosterLink) {
    rosterLink.addEventListener('click', (e) => {
      const rosterSection = document.getElementById('ds-roster-preview');

      if (window.location.pathname === '/' && rosterSection) {
        // Home page: smooth scroll to roster section
        e.preventDefault();
        rosterSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (window.location.pathname !== '/') {
        // Other page: navigate to home with scroll parameter
        e.preventDefault();
        window.location.href = '/?scroll=roster';
      }
      // If on home but no roster section, allow normal link navigation
    });
  }

  // Handle post-navigation scroll on page load
  const params = new URLSearchParams(window.location.search);
  if (params.has('scroll') && params.get('scroll') === 'roster') {
    const rosterSection = document.getElementById('ds-roster-preview');
    if (rosterSection) {
      setTimeout(() => {
        rosterSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState({}, '', '/'); // Clean URL
      }, 100); // Small delay for DOM settlement
    }
  }
}


/* ── 5b. NAV DROPDOWN MENU ────────────────────────────────────────────────── */
/**
 * Initializes all desktop nav dropdowns with:
 * - Hover open/close synced with aria-expanded (CSS drives visual state).
 * - Keyboard: ArrowDown/Up navigates items, Enter opens, Escape closes.
 * - Click-outside closes.
 */
function initNavDropdown() {
  const wrappers = document.querySelectorAll('.ds-nav__item--dropdown');
  if (!wrappers.length) return;

  wrappers.forEach(wrapper => {
    const trigger = wrapper.querySelector('.ds-nav__link--dropdown-trigger');
    const dropdownId = trigger?.getAttribute('aria-controls');
    const dropdown = dropdownId ? document.getElementById(dropdownId) : null;
    if (!trigger || !dropdown) return;

    const links = Array.from(dropdown.querySelectorAll('.ds-nav__dropdown-link'));

    function open() { trigger.setAttribute('aria-expanded', 'true'); }
    function close() { trigger.setAttribute('aria-expanded', 'false'); }

    // Sync aria-expanded with hover so screen readers know the state
    wrapper.addEventListener('mouseenter', open);
    wrapper.addEventListener('mouseleave', close);

    // Keyboard on trigger
    trigger.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        open();
        if (links.length) links[0].focus();
      } else if (e.key === 'Escape') {
        close();
        trigger.focus();
      }
    });

    // Keyboard within dropdown items
    links.forEach((link, i) => {
      link.addEventListener('keydown', (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (i < links.length - 1) links[i + 1].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          i > 0 ? links[i - 1].focus() : trigger.focus();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          close();
          trigger.focus();
        } else if (e.key === 'Home') {
          e.preventDefault();
          links[0].focus();
        } else if (e.key === 'End') {
          e.preventDefault();
          links[links.length - 1].focus();
        }
      });
    });

    // Close when focus leaves the entire dropdown group
    wrapper.addEventListener('focusout', (e) => {
      if (!wrapper.contains(e.relatedTarget)) close();
    });

    // Close on click-outside
    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) close();
    });
  });
}


/* ── 6. DS NAV CART ANIMATION ─────────────────────────────────────────────── */
/**
 * Animates the custom ds-nav cart badge on cart:update.
 *
 * Three simultaneous effects:
 *   1. Badge count updates (and flashes with a spring pop-in).
 *   2. Cart icon jiggles via CSS animation class .ds-nav__cart--animating.
 *   3. Burst-ring pulse via .ds-nav__cart-pulse--fire.
 *
 * Respects prefers-reduced-motion (CSS @media handles it, no JS guard needed).
 */
function initDsNavCartAnimation() {
  const cartLink  = document.getElementById('ds-nav-cart-icon');
  const badge     = document.getElementById('ds-cart-badge');
  const pulseRing = cartLink ? cartLink.querySelector('.ds-nav__cart-pulse') : null;

  if (!cartLink || !badge) return;

  let animTimeout = null;

  document.addEventListener('cart:update', function(e) {
    const detail   = e.detail || {};
    const data     = detail.data || {};
    const itemCount = typeof data.itemCount === 'number' ? data.itemCount : null;

    // Only proceed if we have a count to show
    if (itemCount === null) return;

    // 1. Update badge count
    const newCount = Math.max(0, itemCount);
    if (newCount === 0) {
      badge.textContent = '0';
      badge.classList.add('ds-nav__cart-badge--hidden');
      return;
    }

    // Increment from the currently displayed value when coming from product form
    const comingFromProductForm = data.source === 'product-form-component';
    if (comingFromProductForm) {
      const current = parseInt(badge.textContent, 10) || 0;
      badge.textContent = String(current + newCount);
    } else {
      badge.textContent = String(newCount);
    }

    badge.classList.remove('ds-nav__cart-badge--hidden');

    // 2. Badge spring pop — remove class first to allow re-trigger
    badge.classList.remove('ds-nav__cart-badge--popping');
    // Force reflow so the browser sees the removal
    void badge.offsetWidth;
    badge.classList.add('ds-nav__cart-badge--popping');

    // 3. Jiggle the entire cart link
    cartLink.classList.remove('ds-nav__cart--animating');
    void cartLink.offsetWidth;
    cartLink.classList.add('ds-nav__cart--animating');

    // 4. Burst-ring pulse
    if (pulseRing) {
      pulseRing.classList.remove('ds-nav__cart-pulse--fire');
      void pulseRing.offsetWidth;
      pulseRing.classList.add('ds-nav__cart-pulse--fire');
    }

    // Remove animation classes after they finish so they can retrigger
    clearTimeout(animTimeout);
    animTimeout = setTimeout(function() {
      cartLink.classList.remove('ds-nav__cart--animating');
      badge.classList.remove('ds-nav__cart-badge--popping');
      if (pulseRing) pulseRing.classList.remove('ds-nav__cart-pulse--fire');
    }, 700);
  });
}


/* ── 7. INIT ─────────────────────────────────────────────────────────────── */

document.addEventListener('DOMContentLoaded', () => {
  initNavScrollBehaviour();
  initMobileNav();
  initScrollAnimations();
  initTickerPause();
  initFormValidation();
  initRosterScroll();
  initNavDropdown();
  initDsNavCartAnimation();
});


/* ── 7. MERCH AJAX ADD-TO-CART ───────────────────────────────────────────── */
/**
 * Delegated click handler for .ds-merch-entry__tile-atc buttons ONLY.
 * Uses native Shopify /cart/add.js endpoint.
 * Dispatches 'cart:update' (ThemeEvents.cartUpdate) with itemCount so the
 * native cart-icon component animates the bubble correctly.
 *
 * IMPORTANT: The selector must target only the explicit ATC button, NOT any
 * [data-variant-id] element — variant radio inputs also carry data-variant-id
 * and intercepting their clicks (with e.preventDefault) breaks the size-pill
 * animation in variant-picker.js.
 *
 * No window.scroll — no external libraries.
 */
document.addEventListener('click', function(e) {
  const btn = e.target.closest('.ds-merch-entry__tile-atc[data-variant-id]');
  if (!btn) return;
  e.preventDefault();
  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  btn.setAttribute('aria-busy', 'true');
  btn.textContent = 'ADDING\u2026';

  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  })
  .then(function(r) { return r.json(); })
  .then(function(cartData) {
    btn.textContent = 'ADDED \u2713';
    btn.removeAttribute('aria-busy');

    // Dispatch 'cart:update' (ThemeEvents.cartUpdate) so cart-icon.js animates
    // the bubble. itemCount carries the quantity just added (always 1 here).
    // The detail shape mirrors CartUpdateEvent / CartAddEvent.
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cartData,
        sourceId: 'ds-merch-atc',
        data: {
          source: 'product-form-component', // triggers +1 delta in cart-icon.js
          itemCount: 1,
          didError: false,
        },
      },
    }));

    setTimeout(function() { btn.textContent = 'ADD TO CART'; }, 2000);
  })
  .catch(function() {
    btn.textContent = 'ERROR \u2014 TRY AGAIN';
    btn.removeAttribute('aria-busy');
    setTimeout(function() { btn.textContent = 'ADD TO CART'; }, 2000);
  });
});


/* === T23 ADDITIONS TO dreamstation.js ===
 * Results Page — ds-results-page.liquid · T23
 *
 * 1. initResultsYearFilter — Year tab click → show/hide tbody rows by data-year.
 * 2. initResultsSort       — Sort button click → reorder tbody rows by data-date (ISO).
 *
 * Rules:
 * - No window.addEventListener. No scroll listeners.
 * - Click events only.
 * - Vanilla ES6+ — no libraries.
 * - IntersectionObserver for .ds-animate-in handled by existing initScrollAnimations().
 */

/* ── T23-1. YEAR FILTER TABS ── */
/**
 * Attaches click handlers to all .ds-results-tabs__tab buttons.
 * Active tab receives is-active class and aria-selected="true".
 * Rows with matching data-year are shown; non-matching rows are hidden via aria-hidden.
 * "all" tab restores all rows.
 */
function initResultsYearFilter() {
  const tabList = document.querySelector('.ds-results-tabs');
  if (!tabList) return;

  const tabs  = tabList.querySelectorAll('.ds-results-tabs__tab');
  const tbody = document.getElementById('results-tbody');
  if (!tabs.length || !tbody) return;

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const selectedYear = tab.dataset.filterYear;

      // Update tab active state
      tabs.forEach(t => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');

      // Show / hide rows
      const rows = tbody.querySelectorAll('.ds-results-table__row');
      rows.forEach(row => {
        if (selectedYear === 'all' || row.dataset.year === selectedYear) {
          row.removeAttribute('aria-hidden');
        } else {
          row.setAttribute('aria-hidden', 'true');
        }
      });
    });
  });
}


/* ── T23-2. SORT TOGGLE ── */
/**
 * Attaches a click handler to .ds-results-table-section__sort-btn.
 * Toggles data-sort-dir between "desc" (newest first) and "asc" (oldest first).
 * Reorders all <tr> rows in #results-tbody by their data-date attribute (ISO date string).
 * Uses DocumentFragment for a single reflow.
 */
function initResultsSort() {
  const sortBtn = document.querySelector('.ds-results-table-section__sort-btn[data-sort="date"]');
  if (!sortBtn) return;

  const tbody = document.getElementById('results-tbody');
  if (!tbody) return;

  sortBtn.addEventListener('click', () => {
    const currentDir = sortBtn.dataset.sortDir || 'desc';
    const nextDir    = currentDir === 'desc' ? 'asc' : 'desc';

    sortBtn.dataset.sortDir = nextDir;
    sortBtn.textContent     = nextDir === 'desc' ? 'NEWEST FIRST' : 'OLDEST FIRST';

    // Collect all rows (including hidden ones — filter state is preserved via aria-hidden)
    const rows = Array.from(tbody.querySelectorAll('.ds-results-table__row'));

    rows.sort((a, b) => {
      const dateA = a.dataset.date || '';
      const dateB = b.dataset.date || '';
      if (nextDir === 'asc') {
        return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
      }
      return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
    });

    // Reattach in sorted order using a fragment (single reflow)
    const fragment = document.createDocumentFragment();
    rows.forEach(row => fragment.appendChild(row));
    tbody.appendChild(fragment);
  });
}


/* ── T23 DOMContentLoaded BOOTSTRAP ── */
document.addEventListener('DOMContentLoaded', () => {
  initResultsYearFilter();
  initResultsSort();
});
